<script lang="ts">
  import { CLASSES, type AuraId } from '@arena/shared';
  import {
    auraSeconds,
    type AuraView,
    type FrameView,
    type Pool,
  } from '../../game/hud.svelte';
  import { settings } from '../../lib/settings.svelte';

  let {
    frame,
    compact = false,
    mini = false,
    mirrored = false,
    selected = false,
    onselect,
    onmenu,
    onhover,
    oncancel,
  }: {
    frame: FrameView;
    /** Party and arena frames: buffs and debuffs in rows beside it, the cast bar under it. */
    compact?: boolean;
    /** Target of target: a compact frame without auras or a cast bar, as wide as its container. */
    mini?: boolean;
    /** Compact frames on the right edge put their auras on the left. */
    mirrored?: boolean;
    selected?: boolean;
    onselect?: () => void;
    /** Right click: opens a menu at the cursor. */
    onmenu?: (event: MouseEvent) => void;
    onhover?: (aura: { unitId: string; auraId: AuraId } | null) => void;
    oncancel?: (auraId: AuraId) => void;
  } = $props();

  const hpPct = $derived((frame.hp / frame.maxHp) * 100);
  const pct = (pool: Pool) => (pool.value / Math.max(1, pool.max)) * 100;

  function barText(number: string, percent: number): string {
    const rounded = `${Math.round(percent)}%`;
    if (settings.statusText === 'none') return '';
    if (settings.statusText === 'percent') return rounded;
    if (settings.statusText === 'both') return `${number} (${rounded})`;
    return number;
  }
</script>

{#snippet castBar()}
  {#if frame.cast}
    <div class="bar cast" class:channel={frame.cast.channel}>
      <div class="fill" style="width: {frame.cast.progress * 100}%"></div>
      <span>{frame.cast.name} {frame.cast.remaining.toFixed(1)}</span>
    </div>
  {/if}
{/snippet}

{#snippet auraIcon(aura: AuraView)}
  <button
    class="aura"
    class:harmful={aura.harmful}
    class:cancelable={aura.cancelable && oncancel}
    aria-label={aura.name}
    onclick={() => aura.cancelable && oncancel?.(aura.auraId)}
    onmouseenter={() => onhover?.({ unitId: frame.id, auraId: aura.auraId })}
    onmouseleave={() => onhover?.(null)}
  >
    <img class="icon" src="/icons/{aura.icon}.webp" alt="" />
    {#if aura.remaining > 0}<span class="time"
        >{auraSeconds(aura.remaining)}</span
      >{/if}
  </button>
{/snippet}

<div
  class="frame"
  data-quiet
  class:compact={compact || mini}
  class:mini
  class:mirrored
  class:selected
  class:dead={frame.dead}
  class:hidden={frame.hidden}
  class:enemy={!frame.friendly}
>
  <button
    class="unit"
    onclick={onselect}
    oncontextmenu={(e) => {
      if (!onmenu) return;
      e.preventDefault();
      onmenu(e);
    }}
    disabled={!onselect && !onmenu}
  >
    <span class="portraits">
      <img
        class="portrait"
        src="/icons/{frame.classId}.webp"
        alt=""
        style="border-color: {CLASSES[frame.classId].color}"
      />
      {#if frame.other}<img
          class="other"
          src="/icons/{frame.other}.webp"
          alt={CLASSES[frame.other].name}
          title="Can swap to {CLASSES[frame.other].name}"
          style="border-color: {CLASSES[frame.other].color}"
        />{/if}
    </span>
    <div class="body">
      <div class="top">
        <span class="name" style="color: {CLASSES[frame.classId].color}"
          >{frame.name}</span
        >
        {#if frame.distance !== null}<span class="dist"
            >{Math.round(frame.distance)} yd</span
          >{/if}
      </div>
      <div class="bar hp">
        <div class="fill" style="width: {hpPct}%"></div>
        <span
          >{frame.hidden
            ? 'Stealthed'
            : frame.dead
              ? 'Dead'
              : barText(`${frame.hp} / ${frame.maxHp}`, hpPct)}</span
        >
      </div>
      <div class="bar res {frame.pools[0].type}">
        <div class="fill" style="width: {pct(frame.pools[0])}%"></div>
        {#if !compact && !mini}<span
            >{barText(`${frame.pools[0].value}`, pct(frame.pools[0]))}</span
          >{/if}
      </div>
      {#if frame.other && !compact && !mini}
        <div
          class="bar res {frame.pools[1].type}"
          title="{CLASSES[frame.other].name} {frame.pools[1].type}"
        >
          <div class="fill" style="width: {pct(frame.pools[1])}%"></div>
          <span>{barText(`${frame.pools[1].value}`, pct(frame.pools[1]))}</span>
        </div>
      {/if}
    </div>
  </button>
  {#if mini}{:else if compact}
    <div class="auras">
      <div class="row">
        {#each frame.auras.filter((a) => !a.harmful) as aura (aura.auraId)}
          {@render auraIcon(aura)}
        {/each}
      </div>
      <div class="row">
        {#each frame.auras.filter((a) => a.harmful) as aura (aura.auraId)}
          {@render auraIcon(aura)}
        {/each}
      </div>
    </div>
    {@render castBar()}
  {:else}
    {@render castBar()}
    {#if frame.auras.length > 0}
      <div class="auras">
        {#each frame.auras as aura (aura.auraId)}
          {@render auraIcon(aura)}
        {/each}
      </div>
    {/if}
  {/if}
</div>

<style>
  .frame {
    width: 280px;
    pointer-events: auto;
  }
  .frame:not(.compact) .unit {
    padding: 8px;
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 6px;
  }
  .frame.compact {
    display: grid;
    grid-template-columns: 220px auto;
    grid-template-areas: 'unit auras' 'cast .';
    gap: 2px 4px;
    align-items: start;
    width: auto;
    padding: 0;
    background: none;
    border: none;
  }
  .frame.mini {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'unit';
  }
  .frame.compact.mirrored {
    grid-template-columns: auto 220px;
    grid-template-areas: 'auras unit' '. cast';
  }
  .compact .unit {
    grid-area: unit;
    padding: 5px;
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 6px;
  }
  .compact .auras {
    grid-area: auras;
    flex-direction: column;
    flex-wrap: nowrap;
    margin-top: 0;
  }
  .compact .row {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    min-height: 30px;
    max-width: 228px;
  }
  .mirrored .row {
    flex-direction: row-reverse;
  }
  .compact > .cast {
    grid-area: cast;
    margin-top: 0;
  }
  .unit {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 8px;
    align-items: start;
    width: 100%;
    padding: 0;
    background: none;
    border: none;
    text-align: left;
  }
  .unit:disabled {
    opacity: 1;
    cursor: default;
  }
  .compact .unit {
    column-gap: 6px;
  }
  .selected .unit {
    border-color: #ffd34a;
    box-shadow: 0 0 8px rgba(255, 211, 74, 0.5);
  }
  .frame.dead,
  .frame.hidden {
    filter: grayscale(0.8);
    opacity: 0.7;
  }
  .portraits {
    position: relative;
  }
  .other {
    position: absolute;
    right: -4px;
    bottom: -4px;
    width: 24px;
    height: 24px;
    border: 2px solid;
    border-radius: 50%;
    background: #0a0a0a;
    object-fit: cover;
  }
  .compact .other {
    width: 16px;
    height: 16px;
    border-width: 1px;
  }
  .mini .other {
    display: none;
  }
  .portrait {
    display: block;
    width: 62px;
    height: 62px;
    border: 2px solid;
    border-radius: 50%;
    background: #0a0a0a;
    object-fit: cover;
  }
  .compact .portrait {
    width: 38px;
    height: 38px;
  }
  .mini .portrait {
    width: 30px;
    height: 30px;
  }
  .body {
    min-width: 0;
  }
  .top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 6px;
    font-size: 15px;
    margin-bottom: 3px;
  }
  .compact .top {
    font-size: 13px;
  }
  .name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: bold;
    text-shadow: 0 1px 2px black;
  }
  .dist {
    flex-shrink: 0;
    color: var(--muted);
    font-size: 12px;
  }
  .bar {
    position: relative;
    height: 22px;
    background: #0a0a0a;
    border-radius: 2px;
    overflow: hidden;
    margin-top: 3px;
  }
  .compact .bar {
    height: 14px;
    margin-top: 2px;
  }
  .bar.res {
    height: 13px;
  }
  .compact .bar.res {
    height: 6px;
  }
  .bar.cast {
    height: 18px;
  }
  .bar .fill {
    height: 100%;
    transition: width 0.1s linear;
  }
  .bar span {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    overflow: hidden;
    white-space: nowrap;
    font-size: 13px;
    line-height: 1;
    text-shadow: 0 1px 2px black;
  }
  .bar.res span,
  .bar.cast span,
  .compact .bar span {
    font-size: 11px;
  }
  .hp .fill {
    background: linear-gradient(#4cc94c, #2a8a2a);
  }
  .enemy .hp .fill {
    background: linear-gradient(#e0574a, #a02a20);
  }
  .mana .fill {
    background: var(--mana);
  }
  .rage .fill {
    background: var(--rage);
  }
  .energy .fill {
    background: var(--energy);
  }
  .cast .fill {
    background: linear-gradient(#f3c650, #b98a1c);
    transition: width 0.05s linear;
  }
  .cast.channel .fill {
    background: linear-gradient(#7fd06a, #3f8a2a);
  }
  .auras {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    margin-top: 6px;
  }
  .aura {
    position: relative;
    width: 38px;
    height: 38px;
    border: 1px solid #4caf50;
    border-radius: 3px;
    background: #1c2a1c;
    overflow: hidden;
    padding: 0;
    cursor: default;
  }
  .aura.cancelable {
    cursor: pointer;
  }
  .compact .aura {
    width: 30px;
    height: 30px;
  }
  .aura.harmful {
    border-color: var(--enemy);
    background: #2a1414;
  }
  .aura .icon {
    display: block;
    width: 100%;
    height: 100%;
  }
  .aura .time {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    font-size: 12px;
    font-weight: bold;
    text-align: center;
    color: #ffd34a;
    line-height: 12px;
    text-shadow:
      0 0 2px black,
      0 0 2px black;
  }
</style>
